import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaTruck, FaBatteryFull, FaKey, FaTools, FaClock, FaShieldAlt, FaHeadset, FaDollarSign } from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const services = [
    {
      icon: <FaTruck />,
      title: 'Professional Towing',
      description: 'When it comes to unexpected car trouble, a great towing service can make all the difference.',
      link: '/services#towing'
    },
    {
      icon: <FaBatteryFull />,
      title: 'Battery Boost',
      description: 'With the right professionals on your side, you can get your car up and running in no time.',
      link: '/services#battery'
    },
    {
      icon: <FaKey />,
      title: 'Lock Out Service',
      description: 'With the right professionals on your side, you can have your car unlocked in no time.',
      link: '/services#lockout'
    },
    {
      icon: <FaTools />,
      title: 'Tire Change',
      description: 'A flat tire can happen at any time and can be a major inconvenience, especially when you\'re on the road.',
      link: '/services#tire'
    }
  ];

  const features = [
    {
      icon: <FaClock />,
      title: '24/7 Availability',
      description: 'We\'re always ready to help, day or night.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Insured & Licensed',
      description: 'Fully licensed and insured for your peace of mind.'
    },
    {
      icon: <FaHeadset />,
      title: 'Expert Team',
      description: 'Highly trained professionals with years of experience.'
    },
    {
      icon: <FaDollarSign />,
      title: 'Affordable Rates',
      description: 'Quality service at competitive prices.'
    }
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src="/build/cover.jpg" 
              alt="Toronto Roadside Assistance" 
              className="hero-cover-image"
            />
            <h1 className="hero-title">
              Toronto's Most Trusted <span className="highlight">Roadside Assistance</span>
            </h1>
            <p className="hero-subtitle">
              Professional towing and emergency roadside services available 24/7. 
              Fast response times, expert technicians, and affordable rates.
            </p>
            <div className="hero-buttons">
              <a href="tel:6478988242" className="btn btn-primary">
                Call Now: (647) 898-8242
              </a>
              <Link to="/services" className="btn btn-secondary">
                View Services
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="hero-scroll-indicator">
          <div className="scroll-arrow"></div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section services-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">
              Comprehensive roadside assistance to get you back on the road safely
            </p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <Link to={service.link} className="service-link">
                  Learn More →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section section-dark about-preview">
        <div className="container">
          <div className="about-preview-grid">
            <motion.div
              className="about-preview-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="section-title" style={{ textAlign: 'left' }}>About Us</h2>
              <p className="about-preview-text">
                Toronto Roadside Assistance is a professional towing company located in Toronto, 
                where we pride ourselves on providing top-quality and reliable towing services 
                to all our valued customers. Our team of highly skilled and experienced professionals 
                is dedicated to ensuring that all of our clients receive the best possible assistance 
                whenever they require our services.
              </p>
              <Link to="/about" className="btn btn-primary">
                Learn More About Us
              </Link>
            </motion.div>
            <motion.div
              className="about-preview-features"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-item"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <div className="feature-content">
                    <h4 className="feature-title">{feature.title}</h4>
                    <p className="feature-description">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-title">Need Immediate Assistance?</h2>
            <p className="cta-text">
              Don't wait! Our team is standing by 24/7 to help you with any roadside emergency.
            </p>
            <div className="cta-buttons">
              <a href="tel:6478988242" className="btn btn-primary btn-large">
                Call Us: (647) 898-8242
              </a>
              <Link to="/contact" className="btn btn-secondary btn-large">
                Contact Us Online
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;

